import { useEffect, useState } from 'react';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { fetchEvidenceImage } from './api';

export type EvidenceImageStatus = 'loading' | 'available' | 'unavailable' | 'error';

export interface EvidenceImageResult {
  status: EvidenceImageStatus;
  /** Set only when status === 'available'. Never a live profile image. */
  objectUrl: string | null;
}

const IDLE_RESULT: EvidenceImageResult = { status: 'unavailable', objectUrl: null };

/**
 * The EXACT immutable image an evaluation's image comparison analyzed, as a
 * browser object URL — never the current (possibly since-changed) social
 * profile picture. `status` distinguishes a true 404 ('unavailable' — no
 * stored image for this evaluation/side, expected for e.g. a legacy-path
 * evaluation) from a network/5xx/unexpected failure ('error'), so callers
 * that show a label can pick the right wording; `objectUrl` is only set for
 * 'available'. Callers should render ProfileImage's generic-icon fallback
 * (`genericFallback`) for every status other than 'available' — never
 * initials, never a live profile image.
 *
 * Revokes the previous object URL whenever a new one replaces it or this
 * unmounts, so navigating between candidates never leaks blob URLs.
 */
export function useEvidenceImage(
  evaluationId: string | null | undefined,
  side: 'source' | 'target',
): EvidenceImageResult {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [result, setResult] = useState<EvidenceImageResult>(IDLE_RESULT);

  useEffect(() => {
    if (!evaluationId) {
      setResult(IDLE_RESULT);
      return undefined;
    }

    let cancelled = false;
    let createdUrl: string | null = null;
    setResult({ status: 'loading', objectUrl: null });

    fetchEvidenceImage(discoveryApi, fetchApi, evaluationId, side).then(fetched => {
      if (cancelled) return;
      if (fetched.kind === 'not-found') {
        setResult({ status: 'unavailable', objectUrl: null });
        return;
      }
      if (fetched.kind === 'error') {
        setResult({ status: 'error', objectUrl: null });
        return;
      }
      createdUrl = URL.createObjectURL(fetched.blob);
      setResult({ status: 'available', objectUrl: createdUrl });
    });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [discoveryApi, fetchApi, evaluationId, side]);

  return result;
}
